import { onCleanup, onMount } from "solid-js";

import { isFloatingPanelOpen } from "~/common/floating";
import { IS_APPLE_OS } from "~/util/user_agent";

type Modifier = "Ctrl" | "Alt";
export type Hotkey = `${Modifier}+${string}` | `${Modifier}+${Modifier}+${string}` | (string & {});
type KeyboardCondition = {
  codes: string[];
  // Layout-aware match target compared against `event.key` (null disables key
  // matching — see convertHotkeysToKeyboardConditions).
  key: string | null;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
};

/** Returns OS appropriate, human-readable hotkey string*/
export function formatOSHotKey(windowsHotkey: string): string {
  const forceWindows = document.body.dataset["forceWindowsHotkeys"] === "true";
  if (IS_APPLE_OS && !forceWindows) {
    return windowsHotkey.replace(/Ctrl(\+)?/g, "⌘").replace(/Alt(\+)?/g, "⌥");
  }
  return windowsHotkey;
}

/** The hotkey in `aria-keyshortcuts` syntax, naming the keys the binding actually listens for. */
export function ariaKeyShortcuts(hotkey: Hotkey): string {
  return hotkey.replace(/Ctrl(?=\+)/g, IS_APPLE_OS ? "Meta" : "Control");
}

const SPECIAL_KEY_CODES: Record<string, string> = {
  " ": "Space",
  "-": "Minus",
  "=": "Equal",
  "[": "BracketLeft",
  "]": "BracketRight",
  "\\": "Backslash",
  ";": "Semicolon",
  "'": "Quote",
  "`": "Backquote",
  ",": "Comma",
  ".": "Period",
  "/": "Slash",
};
function keyToCodes(key: string): string[] {
  if (/^[A-Za-z]$/.test(key)) {
    return [`Key${key.toUpperCase()}`];
  }

  // A keypad's Enter is Enter too, for the chords that match by position (Alt on macOS).
  if (key === "Enter") {
    return ["Enter", "NumpadEnter"];
  }

  if (/^[0-9]$/.test(key)) {
    return [`Digit${key}`, `Numpad${key}`];
  }

  const specialKeyCode = SPECIAL_KEY_CODES[key];
  if (specialKeyCode) {
    return [specialKeyCode];
  }

  return [key];
}

function convertHotkeysToKeyboardConditions(hotkey: Hotkey): KeyboardCondition {
  const parts = hotkey.split("+");
  const key = parts[parts.length - 1]!;
  const modifiers = parts.slice(0, parts.length - 1);

  const hasCtrl = modifiers.includes("Ctrl");
  const hasAlt = modifiers.includes("Alt");

  return {
    codes: keyToCodes(key),
    // `codes` name physical QWERTY positions, so on other layouts (AZERTY, QWERTZ,
    // Dvorak, …) the position and the produced character drift apart. The produced
    // character (`event.key`) is authoritative where it's usable, so a remapped
    // layout never fires on a stale QWERTY position. Two chord families transform
    // the character and stay position-only: any Alt chord on macOS (Option+B types
    // ∫), and Ctrl+Alt everywhere (that's AltGr on many layouts — AltGr+E types €).
    key: hasAlt && (IS_APPLE_OS || hasCtrl) ? null : key.toLowerCase(),
    ctrlKey: hasCtrl && !IS_APPLE_OS,
    metaKey: hasCtrl && IS_APPLE_OS,
    altKey: hasAlt,
  };
}

type HotKeyActions = readonly { hotkey: Hotkey; onMatch: (event: KeyboardEvent) => void }[];
type HotkeyBindingOptions = {
  // Skip while focus is in a field, even for modified hotkeys: Alt+digit types a character on
  // some layouts, and a field's own shortcuts should win over the page's.
  ignoreEditable?: boolean;
  // Skip while a modal dialog is open, so page shortcuts do not act behind it.
  ignoreWhenDialogOpen?: boolean;
  // Skip while a popover or menu panel is open, so page shortcuts do not act behind that either.
  ignoreWhenPopoverOpen?: boolean;
  // Listen after the page's other key handlers (bubble phase, not capture) and skip a key one of
  // them already took with preventDefault, so a panel that answers the same keys keeps them.
  yieldToHandledKeys?: boolean;
};

function isEditableTarget(target: EventTarget | null) {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

export function createHotkeyBindings(actions: HotKeyActions, options: HotkeyBindingOptions = {}) {
  const conditions = actions.map((action) => ({
    ...convertHotkeysToKeyboardConditions(action.hotkey),
    onMatch: action.onMatch,
  }));

  const keyHandler = (event: KeyboardEvent) => {
    // Checked before any match so a skipped key keeps its default behaviour.
    if (options.yieldToHandledKeys && event.defaultPrevented) return;
    if (options.ignoreEditable && isEditableTarget(event.target)) return;
    if (options.ignoreWhenDialogOpen && document.querySelector("dialog[open]")) return;

    for (const condition of conditions) {
      // Ignore hotkeys without modifiers when typing in text inputs
      if (
        !condition.metaKey &&
        !condition.ctrlKey &&
        !condition.altKey &&
        (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)
      ) {
        continue;
      }

      // Do all the mods line up and key line up? The layout-aware character
      // (`event.key`) is authoritative when the condition carries one, so a
      // remapped layout (Dvorak, AZERTY, …) only fires on the key that actually
      // types the character. Position (`event.code`) decides only for the
      // conditions that have no usable character (Alt chords on macOS).
      // The DOM type declares `key: string`, but Chromium autofill dispatches
      // keydown events whose key is undefined — the assertion keeps the guard.
      const eventKey = event.key as string | undefined;
      const keyMatches =
        condition.key === null
          ? condition.codes.includes(event.code)
          : eventKey?.toLowerCase() === condition.key;
      if (
        event.metaKey !== condition.metaKey ||
        event.ctrlKey !== condition.ctrlKey ||
        event.altKey !== condition.altKey ||
        !keyMatches
      ) {
        continue;
      }
      // Checked only once a key matches, since it measures every panel on the page.
      if (options.ignoreWhenPopoverOpen && isFloatingPanelOpen()) return;

      event.preventDefault();
      if (!event.repeat) {
        // Don't act when the user holds down the keys.
        condition.onMatch(event);
      }
      break;
    }
  };

  const capture = !options.yieldToHandledKeys;
  document.addEventListener("keydown", keyHandler, { capture });

  return () => {
    document.removeEventListener("keydown", keyHandler, { capture });
  };
}

export function createHotkeyBindingsSolid(actions: HotKeyActions, options?: HotkeyBindingOptions) {
  onMount(() => {
    const cleanup = createHotkeyBindings(actions, options);
    onCleanup(cleanup);
  });
}

// Programmatically fire a hotkey so any global bindings registered with
// createHotkeyBindings will react. Useful for "click this shortcut card" affordances.
export function triggerHotkey(hotkey: Hotkey) {
  const condition = convertHotkeysToKeyboardConditions(hotkey);
  const code = condition.codes[0];
  if (!code) return;
  document.dispatchEvent(
    new KeyboardEvent("keydown", {
      code,
      key: condition.key ?? "",
      altKey: condition.altKey,
      ctrlKey: condition.ctrlKey,
      metaKey: condition.metaKey,
      bubbles: true,
    }),
  );
}
